import { useState } from "react";
import type { Variants } from "framer-motion";

/** Shared easing curves (match the brand's premium, snappy feel). */
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
export const EASE_IN_OUT_SMOOTH = [0.65, 0, 0.35, 1] as const;

/** Staggered-reveal variants for a container + its children. */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07, delayChildren: 0.05 },
  },
};

export const fadeUpItem: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE_OUT_EXPO },
  },
};

const INTRO_SESSION_KEY = "seq_claims_intro_seen";

/** True only when reduced motion is requested. Read synchronously. */
export function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    );
  } catch {
    return false;
  }
}

/**
 * Decides (once, synchronously on first render) whether the brand intro
 * should play. Plays once per browser session; never under reduced motion.
 * Marks the session as "seen" at decision time so reloads skip it.
 */
export function useIntroGate(): boolean {
  const [shouldPlay] = useState<boolean>(() => {
    try {
      if (typeof window === "undefined") return false;
      if (prefersReducedMotion()) return false;
      if (sessionStorage.getItem(INTRO_SESSION_KEY) === "1") return false;
      sessionStorage.setItem(INTRO_SESSION_KEY, "1");
      return true;
    } catch {
      return false;
    }
  });
  return shouldPlay;
}
